import { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

interface CountUpNumberProps {
  /** Raw display value, e.g. "50+", "1,400+", "AI-Powered". Non-numeric values render as-is. */
  value: string;
  className?: string;
  durationMs?: number;
}

/** Splits "1,400+" into { prefix: '', number: 1400, suffix: '+' }. */
function parseValue(value: string) {
  const match = value.match(/^([^\d]*)([\d,.]+)(.*)$/);
  if (!match) return null;
  const numeric = Number(match[2].replace(/,/g, ''));
  if (!Number.isFinite(numeric)) return null;
  return { prefix: match[1], target: numeric, suffix: match[3], hasComma: match[2].includes(',') };
}

export default function CountUpNumber({ value, className, durationMs = 1200 }: CountUpNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.6 });
  const reduceMotion = useReducedMotion();
  const parsed = parseValue(value);
  // Start at the target so crawlers, link previews and screen readers see the
  // real value ("50+"), not "0+". The count-up only replays once in view.
  const [display, setDisplay] = useState(parsed ? parsed.target : null);

  useEffect(() => {
    if (!parsed || !isInView || reduceMotion) return;
    let frame = 0;
    setDisplay(0);
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / durationMs, 1);
      // easeOutExpo for a snappy settle
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      setDisplay(Math.round(parsed.target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView, reduceMotion, value, durationMs]);

  if (!parsed) {
    return <span className={className}>{value}</span>;
  }

  const shown = reduceMotion || display === null ? parsed.target : display;
  const formatted = parsed.hasComma ? shown.toLocaleString('en-AU') : String(shown);

  return (
    <span ref={ref} className={className}>
      {parsed.prefix}
      {formatted}
      {parsed.suffix}
    </span>
  );
}
